import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

const WorkspacesTabPanel = lazy(() => import("./WorkspacesTabPanel"));
const AnalyticsTabPanel = lazy(() => import("./AnalyticsTabPanel"));
const OpportunitiesTabPanel = lazy(() => import("./OpportunitiesTabPanel"));
const CatalogTabPanel = lazy(() => import("./CatalogTabPanel"));
const SegmentsTabPanel = lazy(() => import("./SegmentsTabPanel"));
const GovernanceTabPanel = lazy(() => import("./GovernanceTabPanel"));

function TabPanelSpinner() {
  return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center" }} role="status" aria-live="polite">
      <CircularProgress size={32} />
    </Box>
  );
}

function PanelPaper({ children }) {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        borderColor: "divider",
        overflow: "hidden",
        p: 2.5,
      })}
    >
      {children}
    </Paper>
  );
}

export function WorkspacesSectionPage() {
  const { registerRefresh, stopRefreshing } = useOutletContext();
  /** Atualiza a lista sem remontar o painel (evita fechar o diálogo de criação / resetar o wizard). */
  const workspacesRefreshRef = useRef(() => {});
  useEffect(() => {
    registerRefresh(() => {
      workspacesRefreshRef.current?.();
    });
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  return (
    <PanelPaper>
      <Suspense fallback={<TabPanelSpinner />}>
        <WorkspacesTabPanel refreshRef={workspacesRefreshRef} onLoaded={stopRefreshing} onError={stopRefreshing} />
      </Suspense>
    </PanelPaper>
  );
}

export function InsightsSectionPage() {
  const { registerRefresh, stopRefreshing } = useOutletContext();
  const [analyticsKey, setAnalyticsKey] = useState(0);
  useEffect(() => {
    registerRefresh(() => setAnalyticsKey((k) => k + 1));
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  return (
    <PanelPaper>
      <Suspense fallback={<TabPanelSpinner />}>
        <AnalyticsTabPanel key={analyticsKey} onLoaded={stopRefreshing} onError={stopRefreshing} />
      </Suspense>
    </PanelPaper>
  );
}

export function OpportunitiesSectionPage() {
  const { registerRefresh, stopRefreshing } = useOutletContext();
  const [opportunitiesKey, setOpportunitiesKey] = useState(0);
  useEffect(() => {
    registerRefresh(() => setOpportunitiesKey((k) => k + 1));
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  return (
    <PanelPaper>
      <Suspense fallback={<TabPanelSpinner />}>
        <OpportunitiesTabPanel key={opportunitiesKey} onLoaded={stopRefreshing} onError={stopRefreshing} />
      </Suspense>
    </PanelPaper>
  );
}

export function SegmentsSectionPage() {
  const { registerRefresh, stopRefreshing, setGlobalError, showActionToast } = useOutletContext();
  const [segmentsKey, setSegmentsKey] = useState(0);
  useEffect(() => {
    registerRefresh(() => setSegmentsKey((k) => k + 1));
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  const onError = (msg) => {
    if (msg) setGlobalError(msg);
    stopRefreshing();
  };
  const onSuccess = (msg) => {
    if (msg) showActionToast(msg);
    else stopRefreshing();
  };

  return (
    <PanelPaper>
      <Suspense fallback={<TabPanelSpinner />}>
        <SegmentsTabPanel key={segmentsKey} onLoaded={stopRefreshing} onError={onError} onSuccess={onSuccess} />
      </Suspense>
    </PanelPaper>
  );
}

export function GovernanceSectionPage() {
  const { registerRefresh, stopRefreshing, setGlobalError } = useOutletContext();
  const [governanceKey, setGovernanceKey] = useState(0);
  useEffect(() => {
    registerRefresh(() => setGovernanceKey((k) => k + 1));
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  const onGovernanceError = (msg) => {
    if (msg) setGlobalError(msg);
    stopRefreshing();
  };

  return (
    <PanelPaper>
      <Suspense fallback={<TabPanelSpinner />}>
        <GovernanceTabPanel key={governanceKey} onLoaded={stopRefreshing} onError={onGovernanceError} />
      </Suspense>
    </PanelPaper>
  );
}

export function CatalogSectionPage() {
  const { t } = useTranslation("app");
  const { registerRefresh, stopRefreshing, setGlobalError, showActionToast } = useOutletContext();
  const [catalogKey, setCatalogKey] = useState(0);
  useEffect(() => {
    registerRefresh(() => setCatalogKey((k) => k + 1));
    return () => registerRefresh(() => stopRefreshing());
  }, [registerRefresh, stopRefreshing]);

  const onError = (msg) => {
    if (msg) setGlobalError(msg);
    stopRefreshing();
  };
  const onSuccess = (msg) => {
    if (msg) showActionToast(msg);
    else stopRefreshing();
  };

  return (
    <PanelPaper>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {t("billingPage.adminCatalogHint")}
      </Typography>
      <Suspense fallback={<TabPanelSpinner />}>
        <CatalogTabPanel key={catalogKey} onLoaded={stopRefreshing} onError={onError} onSuccess={onSuccess} />
      </Suspense>
    </PanelPaper>
  );
}
